<template>
  <div class="page">
    <div v-if="submitted" class="blackout">
      <div class="confirmation-modal">
        <h3>Application submitted successfully</h3>
        <br />
        <p>Thank you for your application</p>
        <br />
        <p>Due to the volume of candidates we receive, we regret we cannot contact everyone to confirm the progress of
          their
          application.</p>
        <br />
        <p>You will hear from us in due course if you are selected for interview</p>

        <div class="vue-button-holder">
          <a href="https://fcbstudios.com">
            <div class="back-button">Return to our website</div>
          </a>
        </div>
      </div>
    </div>

    <div v-if="initFailed" class="confirmation-modal">
      <h3>Communication error</h3>
      <br />
      <p>There appears to be a problem communicating with our servers. This may be due to your firewall, or internet connection.</p>
      <br />
      <p>Please refresh the page, and try again. If you continue to see this message, contact us quoting code 'ACC2238'</p>
      <br />
    </div>

    <h3>Apply</h3>
    <h1>{{ application.jobTitle }}</h1>

    <div v-if="resumed" class="resumed-pane">
      We tried to pick your application up where you last left off. If you wish to begin again, just click the button
      below
      <div class="vue-button-holder">
        <div v-on:click="StartOver()" class="btn-start-over">Start over</div>
      </div>
    </div>

    <h3 v-if="application.allowOfficeSelection">Possible locations for this role:</h3>
    <ul class="inline inline--large" v-if="application.allowOfficeSelection">
      <div class="checkbox" v-for="(office, index) in application.offices" :key="index" @click="SelectOffice(office)">
        <Checkbox color="green" :checked="OfficeIsSelected(office)" />
        <p>{{ office.officeName }}</p>
      </div>
    </ul>
    <p class="panel-error">{{ this.validationErrors['offices'] }}</p>
    <br />
    <p v-if="application.isSpeculative">Role selection:</p>
    <ul v-if="application.isSpeculative" class="inline inline--large">
      <div class="checkbox" @click="SetElectedArchitectural(true)">
        <Checkbox :checked="application.architecturalRole" />
        <p>Architectural</p>
      </div>
      <div class="checkbox" @click="SetElectedArchitectural(false)">
        <Checkbox :checked="!application.architecturalRole" />
        <p>Support</p>
      </div>
    </ul>

    <p>
      FCBStudios are committed to ensuring your privacy is protected. You can be assured that the information you provide,
      by which you can be identified, will only be used for the purpose of progressing your application, or to fulfil
      legal or regulatory requirements if necessary. We will not share any of the information you provide with any third
      parties for marketing purposes. We do not collect more information than we need to fulfil our stated purposes and
      will not retain it for longer than is necessary. All data will be destroyed 6 months after receipt.
    </p>
    <br />
    <p><i>* All panels will auto-save.</i></p>

    <!-- Your Details -->
    <div class="panel">
      <div class="panel-header panel-header--hover" :class="GetHeaderClass('details')" @click="TogglePanel('details')">
        <h4>Your Details</h4>
        <DownChevronSVG />
      </div>
      <div class="panel-content panel-content--hover" v-show="ShowPanel('details')">
        <div class="panel-property">
          <div class="panel-label">
            <label>Title (eg Dr, Mr, Mrs, Ms)</label>
          </div>
          <input data-flag="saveApplicantDetails" type="text" v-model="application.title" />
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>First Name*</label>
          </div>
          <input data-flag="saveApplicantDetails" :class="GetValidatedClass('firstName')" type="text" v-model="application.firstName" @change="() => { ValidateProperty('firstName', ValidateEmptyString) }" />
          <p class="panel-error">{{ this.validationErrors['firstName'] }}</p>
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>Surname*</label>
          </div>
          <input data-flag="saveApplicantDetails" :class="GetValidatedClass('surname')" type="text" v-model="application.surname" @change="() => { ValidateProperty('surname', ValidateEmptyString) }" />
          <p class="panel-error">{{ this.validationErrors['surname'] }}</p>
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>Email*</label>
          </div>
          <input data-flag="saveApplicantDetails" :class="GetValidatedClass('email')" type="email" v-model="application.email" @change="() => { ValidateProperty('email', ValidateEmail) }" />
          <p class="panel-error">{{ this.validationErrors['email'] }}</p>
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>Contact Number*</label>
          </div>
          <input data-flag="saveApplicantDetails" :class="GetValidatedClass('contactNumber')" type="tel" v-model="application.contactNumber" @change="() => { ValidateProperty('contactNumber', ValidateTel) }" />
          <p class="panel-error">{{ this.validationErrors['contactNumber'] }}</p>
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>Do You Have The Right To Work In The UK?</label>
          </div>
          <div class="inline pointer" @click="application.hasRightToWorkInUk = !application.hasRightToWorkInUk">
            <Checkbox data-flag="saveApplicantDetails" color="green" size="md" :checked="application.hasRightToWorkInUk" />
            <label class="panel-label--small">I confirm I have the right to work in the UK</label>
          </div>
          <div class="panel-label">
            <label>Additional Information About Your Right To Work (If Any)</label>
          </div>
          <input data-flag="saveApplicantDetails" type="text" v-model="application.additionalRightToWorkInfo" />
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>Do you require visa support for this application?</label>
          </div>
          <div class="inline pointer" @click="SetVisaSupport(!application.requiresVisaSupport)">
            <Checkbox data-flag="saveApplicantDetails" color="green" size="md" :checked="application.requiresVisaSupport" />
            <label class="panel-label--small">Yes, I would need visa support for this application</label>
          </div>
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>When are you available to start work?*</label>
          </div>
          <div class="panel-label--small">
            <label>Please either select a date</label>
          </div>
          <input data-flag="saveApplicantDetails" type="date" v-model="application.startDate" @change="() => { ValidateProperty('startDate', ValidateStartDate) }" />
          <p class="panel-error">{{ this.validationErrors['startDate'] }}</p>
          <div class="panel-label--small">
            <label>Or provide the number of weeks you must provide in notice to your current employer</label>
          </div>
          <input data-flag="saveApplicantDetails" type="number" v-model="application.weeksNotice" @change="() => { ValidateProperty('startDate', ValidateStartDate) }" />
        </div>
      </div>
    </div>

    <!-- Architectural Education & Software Skills -->
    <div class="panel" v-if="IsArchitectural()">
      <div class="panel-header panel-header--hover" :class="GetHeaderClass('education')" @click="TogglePanel('education')">
        <h4>Architectural Education & Software Skills</h4>
        <DownChevronSVG />
      </div>
      <div class="panel-content panel-content--hover" v-show="ShowPanel('education')">
        <div class="panel-property">
          <div class="panel-label">
            <label>Part 1 year of completion (supply estimated if not yet complete)*:</label>
          </div>
          <input data-flag="saveSkills" :class="GetValidatedClass('partOneYear')" type="number" v-model.number="application.partOneYear" @change="() => { ValidateProperty('partOneYear', ValidateNotZero) }" />
          <p class="panel-error">{{ this.validationErrors['partOneYear'] }}</p>
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>Part 2 year of completion (if any, leave as 0 if not yet completed):</label>
          </div>
          <input v-on:change='SaveApplication("saveSkills")' type="number" v-model.number="application.partTwoYear" />
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>Part 3 year of completion (if any, leave as 0 if not yet completed):</label>
          </div>
          <input data-flag="saveSkills" type="number" v-model.number="application.partThreeYear" />
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>Please indicate your current level of competency in the following software:</label>
            <div v-for="skill in application.skills">
              <div class="skill-line">{{ skill.skillName }}
                <select data-flag="saveSkills" v-model="skill.indicatedSkillLevel">
                  <option v-for="so in skillsOutcomes" v-bind:value="so.outcomeId">
                    {{ so.outcomeName }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Applications, Documents and Evidence -->
    <div class="panel">
      <div class="panel-header panel-header--hover" :class="GetHeaderClass('documents')" @click="TogglePanel('documents')">
        <h4>Applications, Documents and Evidence</h4>
        <DownChevronSVG />
      </div>
      <div class="panel-content panel-content--hover" v-show="ShowPanel('documents')">
        <div class="panel-property panel-property--full-width" v-if="IsArchitectural()">
          <div class="panel-label">
            <label>An image of your best work (optional)</label>
          </div>
          <FileInput :showBackup="true" :files="bestworkFiles" @set="SaveApplicationLink(...arguments, 2)" :allowedFileTypes="['jpg', 'gif', 'png']" :sizeLimit="2" />
          <p class="panel-error">{{ this.validationErrors['bestWorkPath'] }}</p>
          <div class="panel-label--small">
            <i>
              <label>This is an opportunity to showcase one of your best bits of work. Please select a single image
                (jpg/gif/png) of no more than 2MB. Do not use this to show us your entire portfolio, or a personal
                image.</label>
            </i>
          </div>
        </div>
        <div class="panel-property panel-property--full-width">
          <div class="panel-label">
            <label>Covering letter & CV*</label>
          </div>
          <FileInput :showBackup="true" :files="cvcoveringFiles" @set="SaveApplicationLink(...arguments, 1)" :sizeLimit="2" />
          <p class="panel-error">{{ this.validationErrors['coveringLetterCvPath'] }}</p>
          <div class="panel-label--small">
            <i>
              <label>One document that contains both your CV and covering letter (if any). PDF format preferred.</label>
            </i>
          </div>
        </div>
        <div class="panel-property panel-property--full-width" v-if="IsArchitectural()">
          <div class="panel-label">
            <label>Portfolio</label>
          </div>
          <FileInput :showBackup="true" :files="portfolioFiles" @set="SaveApplicationLink(...arguments, 3)" :sizeLimit="10" />
          <p class="panel-error">{{ this.validationErrors['portfolioPath'] }}</p>
          <div class="panel-label--small">
            <i>
              <label>Please note: larger files may take a while to upload, and our limit is set with the expectation that
                you have a reasonable internet connection. The panel will confirm the receipt of your files on our
                servers. Please be patient.</label>
            </i>
          </div>
          <div class="panel-label--small">
            <i>
              <label>On mobile data, please provide your portfolio via the 'online portfolio' section instead</label>
            </i>
          </div>
        </div>
        <div class="panel-property panel-property--full-width">
          <div class="panel-label">
            <label>Address for an online CV/Portfolio (if any)</label>
          </div>
          <input data-flag="saveDocuments" type="text" placeholder="www.myportfoliosite.com" v-model="application.websitePortfolioPath" />
          <div class="panel-label--small">
            <i>
              <label>We regret that there is not always time to review expansive website CVs/Portfolios in full, though we do
                endeavour to look at every one. Please ensure all application-critical details are in the documents you
                provide</label>
            </i>
          </div>
        </div>
      </div>
    </div>

    <!-- Application Details -->
    <div class="panel">
      <div class="panel-header panel-header--hover" :class="GetHeaderClass('application')" @click="TogglePanel('application')">
        <h4>Application Details</h4>
        <DownChevronSVG />
      </div>
      <div class="panel-content panel-content--hover" v-show="ShowPanel('application')">
        <div class="panel-property panel-property--full-width">
          <div class="panel-label">
            <label>Are there any reasonable adjustments we can make to assist you with working or interviewing?</label>
          </div>
          <textarea v-on:change='SaveApplication("saveDetails")' type="text" v-model="application.reasonableAdjustments"></textarea>
          <br />
          <i>
            <div class="panel-label--small">
              <label>
                Please tell us if there are any reasonable adjustments we can make :
              </label>
            </div>
            <ol class="panel-label--small" type="1">
              <li>to assist you in your application and/or interview</li>
              <li>to the job itself to help you carry it out</li>
            </ol>
          </i>
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>Where did you hear about this position?</label>
          </div>
          <select data-flag="saveDetails" v-model="application.heardAboutId">
            <option v-for="(item, index) in heardAbout" :key="index" :value="item.heardAboutId">
              {{ item.heardAboutFrom }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Equal Opportunities -->
    <div class="panel">
      <div class="panel-header panel-header--hover" :class="GetHeaderClass('equal')" @click="TogglePanel('equal')">
        <h4>Equal Opportunities</h4>
        <DownChevronSVG />
      </div>
      <div class="panel-content panel-content--hover" v-show="ShowPanel('equal')">
        <div>
          <p>
            <i>Feilden Clegg Bradley Studios is an equal opportunity employer and aims to have an inclusive work
              environment.</i>
          </p>
          <br>
          <p><i>
              While it is voluntary to complete the below monitoring form, by providing the information you help our
              Practice better understand our workforce composition, examine our processes, and meet our obligations under
              the Equality Act 2010 and our Equality, Diversity and Inclusion policy.</i></p>
          <br>
          <p><i>
              All information will be treated in confidence and will not be seen by anyone directly involved in the
              appointment. Your answers will be used for monitoring purposes only and stored in compliance with the Data
              Protection Act 2018 and FCBS’s GDPR Policy.</i></p>

          <br v-if="BelfastIsSelected()">
          <p v-if="BelfastIsSelected()">We monitor the community background of our job applicants in Northern Ireland in
            order to demonstrate our
            commitment to promoting equality of opportunity in employment and to comply with our duties under the Fair
            Employment & Treatment (NI) Order 1998.</p>
          <br v-if="BelfastIsSelected()">
          <p v-if="BelfastIsSelected()">You are not obliged to answer the questions on this form and you will not suffer
            any penalty if you choose
            not to do so. Nevertheless, we encourage you to answer these questions. Your answers will be used by us to
            prepare and submit a monitoring return to the Equality Commission, but your identity will be kept anonymous.
            In all other regards your answers will be treated with the strictest confidence. We assure you that your
            answers will not be used by us to make any decisions affecting you, whether during the recruitment or while
            employed by us.</p>

        </div>
        <hr />
        <div class="panel-property">
          <div class="panel-label">
            <label>Date of Birth</label>
          </div>
          <input data-flag="saveEqualOps" type="date" v-if="!application.equalOps.preferNotDateOfBirth" v-model="application.equalOps.dateOfBirth" />
          <div class="inline pointer" @click="application.equalOps.preferNotDateOfBirth = !application.equalOps.preferNotDateOfBirth">
            <Checkbox data-flag="saveEqualOps" color="green" size="md" :checked="application.equalOps.preferNotDateOfBirth" />
            <label class="panel-label--small">I'd prefer not to say</label>
          </div>
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>What is your gender?</label>
          </div>
          <select data-flag="saveEqualOps" v-model="application.equalOps.selectedGenderId">
            <option v-for="(gender, index) in genders" :key="index" :value="gender.genderId">
              {{ gender.genderName }}
            </option>
          </select>
          <input type="text" data-flag="saveEqualOps" v-if="application.equalOps.selectedGenderId == 4" v-model="application.equalOps.genderSelfDescription" placeholder="please self-describe" />
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>What is your race/ethnicity?</label>
          </div>
          <select data-flag="saveEqualOps" v-model="application.equalOps.selectedRaceId">
            <option v-for="(race, index) in races" :key="index" :value="race.raceId">
              {{ race.raceName }}
            </option>
          </select>
          <input type="text" data-flag="saveEqualOps" v-if="application.equalOps.selectedRaceId == 18" v-model="application.equalOps.raceSelfDescription" placeholder="please self-describe" />
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>What is your sexual orientation?</label>
          </div>
          <select data-flag="saveEqualOps" v-model="application.equalOps.sexualOrientationId">
            <option v-for="(orientation, index) in orientations" :key="index" :value="orientation.sexualOrientationId">
              {{ orientation.sexualOrientationName }}
            </option>
          </select>
          <input type="text" data-flag="saveEqualOps" v-if="application.equalOps.sexualOrientationId == 6" v-model="application.equalOps.orientationSelfDescription" placeholder="please self-describe" />
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>What is your religion/faith?</label>
          </div>
          <select data-flag="saveEqualOps" v-model="application.equalOps.selectedFaithId">
            <option v-for="(faith, index) in faiths" :key="index" :value="faith.faithId">
              {{ faith.faithName }}
            </option>
          </select>
          <input type="text" data-flag="saveEqualOps" v-if="application.equalOps.selectedFaithId == 11" v-model="application.equalOps.faithSelfDescription" placeholder="please self-describe" />
        </div>
        <div class="panel-property" v-if="BelfastIsSelected()">
          <div class="panel-label">
            <label>What is your faith community (NI applicants)?</label>
          </div>

          <div class="explanatoryText">
            <p>Regardless of whether they actually practice a religion, most people in Northern Ireland are perceived to
              be members of either the Protestant or Roman Catholic communities.</p>
          </div>

          <p>Please indicate the community to which you belong by selecting an option from the list below:</p>

          <select data-flag="saveEqualOps" v-model="application.equalOps.selectedNiCommunityId">
            <option value="1">No community selected</option>
            <option value="2">Protestant community</option>
            <option value="3">Roman Catholic community</option>
            <option value="4">Neither of the Protestant or Roman Catholic communities</option>
          </select>
          <div class="explanatoryText">
            If you do not answer the above question, we are encouraged by the Equality Commission for Northern Ireland to
            use the residuary method of making a determination, which means that we can make a determination as to your
            community background on the basis of the personal information supplied by you in your application
            form/personnel file.
          </div>

        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>Do you consider yourself to have a disablity or impairment?</label>
          </div>
          <select data-flag="saveEqualOps" v-model="application.equalOps.selectedDisabledStatus">
            <option value="1">Prefer not to say</option>
            <option value="2">Yes</option>
            <option value="3">No</option>
          </select>
        </div>
        <div class="panel-property">
          <div class="panel-label">
            <label>Nationality</label>
          </div>
          <input data-flag="saveEqualOps" v-if="!application.equalOps.preferNotNationality" type="text" v-model="application.equalOps.nationalitySelfDescription" />
          <div class="inline pointer" @click="application.equalOps.preferNotNationality = !application.equalOps.preferNotNationality">
            <Checkbox data-flag="saveEqualOps" color="green" size="md" :checked="application.equalOps.preferNotNationality" />
            <label class="panel-label--small">I'd prefer not to say</label>
          </div>
        </div>
      </div>
    </div>

    <div class="panel">
      <div class="panel-header">
        <h4>Submit Your Application</h4>
      </div>
      <div class="panel-content">
        <div class="panel-label">
          <label>Please complete all required fields, and then you will be able to submit your application</label>
        </div>

        <button v-if="IsValidated() && !submitted" @click="SubmitApplication">Submit</button>
        <p v-if="submitted">Application submitted successfully.</p>
        <p v-if="submitFailed">Application failed to submit, please try again or contact administration.</p>
      </div>
    </div>
  </div>
</template>

<script>
import $fetch from '@/js/fetch'
import Vue from 'vue'
import DownChevronSVG from '@/assets/icons/DownChevron.svg'
import Application from '@/js/models/application'
import Checkbox from '@/components/Checkbox'
import FileInput from '@/components/FileInput'
import { setCookie, getCookie, eraseCookie } from '@/js/cookie'
export default {
  components: {
    DownChevronSVG,
    Checkbox,
    FileInput
  },
  data() {
    return {
      application: new Application({}),
      offices: [],
      heardAbout: [],
      skills: [],
      skillsOutcomes: [],
      faiths: [],
      orientations: [],
      races: [],
      genders: [],
      roles: [],
      validationErrors: {},
      resumed: false,
      interactedWith: {},
      panels_hidden: ['debug'],
      saveTimeout: null,
      saveDelay: 3000,
      cvcoveringFiles: [],
      bestworkFiles: [],
      portfolioFiles: [],
      submitted: false,
      submitFailed: false,
      initFailed: false
    }
  },
  watch: {
    application: {
      deep: true,
      handler() { }
    },
    validationErrors: {
      deep: true,
      handler() { }
    },
    interactedWith: {
      deep: true,
      handler() { }
    }
  },
  async mounted() {

    await this.InitialiseApplication()

    await this.GetHeardAbout()
    await this.GetSkillsList()
    await this.GetSkillsOutcomesList()
    await this.GetFaithsList()
    await this.GetOrientationsList()
    await this.GetRacesList()
    await this.GetGendersList()
    await this.GetRoleTypeList()
    this.ApplySaveEvents()
  },
  methods: {
    ApplyJobId() {
      if (this.$route.query.roleId !== undefined && this.$route.query.roleId.length > 0 && this.$route.query.roleId.length < 39) {
        this.application.jobId = this.$route.query.roleId;
      }
    },
    // Client side
    ApplySaveEvents() {
      const inputs = document.querySelectorAll('input:not([type="checkbox"])')
      const selects = document.querySelectorAll('select')
      const all = Array.from(inputs).concat(Array.from(selects))
      for (let input of all) {
        const saveFlagProperty = input.getAttribute("data-flag")
        input.addEventListener('blur', () => {
          /*
          if (this.saveTimeout !== null) {
            clearTimeout(this.saveTimeout)
            this.saveTimeout = null
          }
          this.saveTimeout = setTimeout(this.SaveApplication(saveFlagProperty), this.saveDelay)
          */
          this.SaveApplication(saveFlagProperty)
        })
        /*
        input.addEventListener('focus', () => {
          clearTimeout(this.saveTimeout)
          this.saveTimeout = null
        })
        */
      }
    },
    GetValidatedIcon(property) {
      return ''
    },
    GetHeaderClass(id) {
      if (!this.ShowPanel(id)) return 'panel-header--closed'
      return ''
    },
    TogglePanel(id) {
      let index = this.panels_hidden.indexOf(id)
      if (index > -1) this.panels_hidden.splice(index, 1)
      else this.panels_hidden.push(id)
    },
    ShowPanel(id) {
      return this.panels_hidden.indexOf(id) === -1
    },
    OfficeIndex(office) {
      // { "officeId": 1, "officeName": " Bath ", "selected": false }
      let index = -1
      for (let [i, selectedOffice] of this.application.offices.entries()) {
        if (selectedOffice.officeId === office.officeId) index = i
      }
      return index
    },
    BelfastIsSelected() {
      if (this.application.jobTitle.includes("Belfast")) {
        return true;
      }
      let office = { "officeId": 3, "officeName": "Belfast", "selected": false }
      let index = this.OfficeIndex(office)
      if (index > -1) {
        if (this.application.offices[index].selected) return true
        return false
      } else return false
    },
    OfficeIsSelected(office) {
      let index = this.OfficeIndex(office)
      if (index > -1) {
        if (this.application.offices[index].selected) return true
        return false
      } else return false
    },
    async SelectOffice(office) {
      let index = this.OfficeIndex(office)
      if (index > -1) {
        office = this.application.offices[index]
        Vue.set(office, 'selected', !office.selected)
      } else {
        office = JSON.parse(JSON.stringify(office))
        office.selected = true
        this.application.offices.push(office)
      }
      if (this.application.onlyOneOffice) {
        for (let selectedOffice of this.application.offices) {
          if (selectedOffice.officeId !== office.officeId) Vue.set(selectedOffice, 'selected', false)
        }
      }
      this.ValidateProperty('offices', this.ValidateOffices)
      await this.SaveApplication()
    },
    async SetElectedArchitectural(bool) {
      Vue.set(this.application, 'userElectedArchitectural', bool)
      Vue.set(this.application, 'architecturalRole', bool)
      this.ForceInteractedWith()
      await this.SaveApplication()
    },
    async SetVisaSupport(bool) {
      Vue.set(this.application, 'requiresVisaSupport', bool)

      this.ForceInteractedWith()
      await this.SaveApplication()
    },

    // Pulling data
    async GetSkillsList() {
      this.skills = await $fetch({ ctx: this }, `${this.$store.state.API_URL}/Applications/GetSkillsList`)
    },
    async GetSkillsOutcomesList() {
      this.skillsOutcomes = await $fetch({ ctx: this }, `${this.$store.state.API_URL}/Applications/GetSkillsOutcomesList`)
    },
    async GetFaithsList() {
      this.faiths = await $fetch({ ctx: this }, `${this.$store.state.API_URL}/Applications/GetFaithsList`)
    },
    async GetOrientationsList() {
      this.orientations = await $fetch({ ctx: this }, `${this.$store.state.API_URL}/Applications/GetOrientationsList`)
    },
    async GetRacesList() {
      this.races = await $fetch({ ctx: this }, `${this.$store.state.API_URL}/Applications/GetRacesList`)
    },
    async GetGendersList() {
      this.genders = await $fetch({ ctx: this }, `${this.$store.state.API_URL}/Applications/GetGendersList`)
    },
    async GetRoleTypeList() {
      this.roles = await $fetch({ ctx: this }, `${this.$store.state.API_URL}/Applications/GetRoleTypeList`)
    },
    async GetHeardAbout() {
      this.heardAbout = await $fetch({ ctx: this }, `${this.$store.state.API_URL}/Applications/GetHeardAbout`)
    },
    // Validation
    /**
     * ValidateProperty
     * @param {*} property: Any
     * @param {*} validator: Function
     */
    GetValidatedClass(property) {
      let errorClass = 'panel-input--error'
      let successClass = 'panel-input--success'
      if (!this.interactedWith[property]) return ''
      else return this.validationErrors[property] ? errorClass : successClass
    },
    ValidateProperty(property, validator) {
      let data = this.application
      if (!validator) return
      let message = validator(data[property])
      Vue.set(this.interactedWith, property, true)
      if (message != null) {
        Vue.set(this.validationErrors, property, message)
      } else if (this.validationErrors[property]) {
        Vue.set(this.validationErrors, property, null)
        delete this.validationErrors[property]
      }
    },
    ValidateEmptyString(string) {
      let validated = string.match(/\p{L}+/u)
      if (!validated || validated.length === 0) return 'Field cannot be empty.'
      return null
    },
    ValidateEmail(email) {
      if (email.length === 0) return 'Field cannot be empty'
      let validated = String(email)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
      if (!validated) return 'Valid email is required.'
      return null
    },
    ValidateTel(tel) {
      let validated = tel.match(/\d/g)
      if (!validated || (validated.length !== 10 && validated.length !== 11)) return 'Valid telephone number is required.'
      return null
    },
    ValidateStartDate(date) {
      let defaultDate = '0001-01-01T00:00:00'
      if (date === defaultDate && this.application.weeksNotice == 0) return 'Please enter a valid start date or weeks of notice.'
      return null
    },
    ValidateNotZero(value) {
      if (parseFloat(value) <= 0) return 'Please enter a value.'
      return null
    },
    ValidateOffices() {
      if (!this.application.allowOfficeSelection) return
      console.log(this.application.offices)
      for (let office of this.application.offices) {
        if (office.selected) return null
      }
      return 'Please select one or more offices if applicable.'
    },
    ForceInteractedWith() {
      this.ValidateProperty('offices', this.ValidateOffices)
      this.ValidateProperty('firstName', this.ValidateEmptyString)
      this.ValidateProperty('surname', this.ValidateEmptyString)
      this.ValidateProperty('email', this.ValidateEmail)
      this.ValidateProperty('contactNumber', this.ValidateTel)
      this.ValidateProperty('startDate', this.ValidateStartDate)
      if (!this.IsArchitectural()) return
      this.ValidateProperty('coveringLetterCvPath', this.ValidateEmptyString)
      this.ValidateProperty('partOneYear', this.ValidateNotZero)
    },
    IsValidated() {
      let validated = true
      if (this.validationErrors['offices']) validated = false
      if (this.validationErrors['firstName'] || !this.interactedWith['firstName']) validated = false
      if (this.validationErrors['surname'] || !this.interactedWith['surname']) validated = false
      if (this.validationErrors['email'] || !this.interactedWith['email']) validated = false
      if (this.validationErrors['contactNumber'] || !this.interactedWith['contactNumber']) validated = false
      if (this.validationErrors['startDate'] || !this.interactedWith['startDate']) validated = false
      if (!this.IsArchitectural()) return validated
      // Architects only.
      if (this.validationErrors['coveringLetterCvPath'] || !this.interactedWith['coveringLetterCvPath']) validated = false
      if (this.validationErrors['partOneYear'] || !this.interactedWith['partOneYear']) validated = false
      return validated
    },
    // Submit
    async SaveApplicationLink(files, type) {
      if (files.length === 0) return
      files = Array.from(files)
      const file = files[0]
      const id = this.application.applicationKey

      let url = `${this.$store.state.API_URL}/Applications/SaveApplicationLink`
      let data = { id, typeId: type }
      let result = { contractSuccessful: false }
      this.$store.commit('AddLoading', 'application-link')
      try {
        result = JSON.parse(await new Promise(function (resolve, reject) {
          var xhttp = new XMLHttpRequest();
          xhttp.open("POST", url);
          let fd = new FormData();
          fd.append('vessel', JSON.stringify(data))
          fd.append('file', file)
          xhttp.onload = function () {
            if (xhttp.status == 200) {
              resolve(xhttp.responseText);
            }
            else {
              reject(xhttp.status);
            }
          };
          xhttp.send(fd);
        }))
      } catch (err) {
        console.error(err)
      }
      this.$store.commit('RemoveLoading', 'application-link')

      let property
      if (type == 1) property = 'coveringLetterCvPath'
      if (type == 2) property = 'bestWorkPath'
      if (type == 3) property = 'portfolioPath'
      if (result.contractSuccessful) {
        if (type == 1) this.cvcoveringFiles = files
        if (type == 2) this.bestworkFiles = files
        if (type == 3) this.portfolioFiles = files
        this.application[property] = result?.returnModel?.savedName
        this.ValidateProperty(property, this.ValidateEmptyString)
        this.SaveApplication('saveDocuments')
      } else {
        Vue.set(this.validationErrors, property, 'File upload failed, please try again or contact administrator')
      }
    },
    async SaveApplication(saveFlagProperty) {
      // if (!this.IsValidated()) return
      const application = JSON.parse(JSON.stringify(this.application))
      application[saveFlagProperty] = true
      const result = await $fetch(
        {
          ctx: this,
          method: 'POST',
          body: application
        },
        `${this.$store.state.API_URL}/Applications/SaveApplication`
      )
    },
    async SubmitApplication() {
      this.submitFailed = false
      const applicationId = this.application.applicationKey
      const result = await $fetch({ ctx: this }, `${this.$store.state.API_URL}/Applications/SubmitApplication?applicationId=${applicationId}`)

      if (result.contractSuccessful) {
        this.submitted = true
        this.ClearApplication()
      } else this.submitFailed = true
    },
    GetApplicationKey() {
      const applicationKey = getCookie('applicationKey');
      if (applicationKey !== undefined && applicationKey !== null) {
        var parts = applicationKey.split("|");
        this.application.applicationKey = parts[0];
        this.application.roleId = parts[1];

        if (this.$route.query.roleId !== undefined && this.$route.query.roleId !== this.application.roleId) {
          this.application.roleId = this.$route.query.roleId;
          this.application.applicationKey = '00000000-0000-0000-0000-000000000000';
        }
      }
      else {
        this.application.roleId = this.$route.query.roleId !== undefined ? this.$route.query.roleId : '00000000-0000-0000-0000-000000000000';
        this.application.applicationKey = '00000000-0000-0000-0000-000000000000';
      }
    },
    StoreApplicationKey() {
      const applicationKey = `${this.application.applicationKey}|${this.application.jobId}`
      setCookie('applicationKey', applicationKey, 20)
    },
    StartOver() {
      this.ClearApplication();
      this.InitialiseApplication();
    },
    ClearApplication() {
      eraseCookie('applicationKey')
      this.application.applicationKey = '00000000-0000-0000-0000-000000000000'
      this.application.roleId = '00000000-0000-0000-0000-000000000000'
    },
    async InitialiseApplication() {
      this.resumed = false;
      this.GetApplicationKey();

      const roleId = this.application.roleId;
      const applicationKey = this.application.applicationKey;

      let query = ``
      if (roleId !== '00000000-0000-0000-0000-000000000000') query += `?roleId=${roleId}`

      if (applicationKey !== '00000000-0000-0000-0000-000000000000' && applicationKey) {
        if (query.length === 0) query += `?applicationKey=${applicationKey}`
        else query += `&applicationKey=${applicationKey}`
      }

      const result = await $fetch({ ctx: this }, `${this.$store.state.API_URL}/Applications/InitialiseApplication${query}`)

      if (result.notFound) {
        this.application.applicationKey = '00000000-0000-0000-0000-000000000000'
        await this.InitialiseApplication()
        return
      }
      if (result.failed) {
        this.initFailed = false;
      }
      if (result.rejected) {
        this.initFailed = false;
      }

      if (result.displayApplication) {
        this.application = result.displayApplication
        this.StoreApplicationKey()
      }

      if (result.resumed) {
        this.ForceInteractedWith();
        this.resumed = true;
      }
      if (!this.application.allowOfficeSelection) this.ValidateProperty('offices', this.ValidateOffices)
      this.ApplyJobId()
    },
    IsArchitectural() {
      return this.application.architecturalRole;
    }
  },
}
</script>